import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import { TrophyIcon, SkullIcon } from "@phosphor-icons/react";

const TIER_COLORS = {
  champion: "#D6FF38",   // primary accent, brightest
  elite:    "#60A5FA",   // blue
  basic:    "#94A3B8",   // muted
  testing:  "#475569",   // dark
  graveyard:"#FF4D4D",   // red
};

function heatColor(v) {
  if (v >= 30) return "#2ECA7F";
  if (v >= 10) return "rgba(46,202,127,0.65)";
  if (v >= 0)  return "rgba(46,202,127,0.35)";
  if (v >= -10) return "rgba(255,77,77,0.35)";
  if (v >= -30) return "rgba(255,77,77,0.65)";
  return "#FF4D4D";
}

// v6.5.0: family-progress sparkline. Renders one stacked bar per strategy
// family showing how close that family is to the 10-champions-per-cell goal.
function FamilySparkline({ progress, target = 10 }) {
  if (!Array.isArray(progress) || progress.length === 0) {
    return <div className="text-[10px] mono text-[#475569]">no family data yet</div>;
  }
  return (
    <div className="space-y-1.5" data-testid="family-progress-strip">
      {progress.slice(0, 6).map((row) => {
        const champProgress = Math.min(1, (row.champion || 0) / target);
        const totalBots = row.total || 0;
        return (
          <div key={row.family} className="flex items-center gap-2" data-testid={`family-row-${row.family}`}>
            <div className="mono text-[10px] text-[#94A3B8] uppercase tracking-widest w-10 shrink-0">{row.family}</div>
            <div className="flex-1 h-2 rounded-sm bg-[#0F1015] relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 transition-all"
                style={{ width: `${champProgress * 100}%`, background: TIER_COLORS.champion }}
                data-testid={`family-champion-fill-${row.family}`}
              />
              {row.elite > 0 && (
                <div
                  className="absolute inset-y-0"
                  style={{
                    left: `${champProgress * 100}%`,
                    width: `${Math.min(1, row.elite / target) * 100}%`,
                    background: TIER_COLORS.elite,
                    opacity: 0.7,
                  }}
                />
              )}
            </div>
            <div className="mono text-[10px] text-[#F1F5F9] w-16 shrink-0 text-right">
              <span style={{ color: row.champion >= target ? TIER_COLORS.champion : "#F1F5F9" }}>{row.champion || 0}</span>
              <span className="text-[#475569]">/{target}</span>
              <span className="text-[#64748B] ml-1">({totalBots})</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Symbols() {
  const [symbols, setSymbols] = useState([]);
  const [groupByBase, setGroupByBase] = useState(true);

  useEffect(() => {
    api.get(`/symbols?group_by_base=${groupByBase}`)
      .then((r) => setSymbols(r.data.symbols))
      .catch(console.error);
  }, [groupByBase]);

  return (
    <div data-testid="symbols-page" className="p-6 space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Asset Coverage</div>
          <h1 className="text-2xl tracking-tight font-medium mt-1">Symbols</h1>
          <div className="text-[11px] mono text-[#64748B] mt-1">
            Sparkline strip per tile shows 10-champion goal progress by family.
            <Link to="/heatmap" className="text-[#D6FF38] hover:underline ml-2" data-testid="heatmap-link">
              Portfolio Heatmap &rarr;
            </Link>
          </div>
        </div>
        <label className="flex items-center gap-2 mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
          <input
            type="checkbox"
            checked={groupByBase}
            onChange={(e) => setGroupByBase(e.target.checked)}
            className="accent-[#D6FF38]"
            data-testid="group-by-base-toggle"
          />
          Group SB variants together
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {symbols.map((s) => (
          <Link
            key={s.symbol}
            to={`/leaderboard?symbol=${encodeURIComponent(s.symbol)}`}
            data-testid={`symbol-card-${s.symbol}`}
            className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4 hover:border-[#D6FF38]/40 transition-colors group flex flex-col"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="min-w-0">
                <div className="mono text-xl text-[#F1F5F9] flex items-center gap-2">
                  <span className="truncate">{s.symbol}</span>
                  {s.variants && s.variants.length > 1 && (
                    <span className="text-[9px] mono uppercase text-[#D6FF38] bg-[#D6FF38]/10 px-1.5 py-0.5 rounded-sm">
                      {s.variants.length} var
                    </span>
                  )}
                </div>
                <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">
                  {s.total_bots} bots tested
                </div>
              </div>
              <div className="w-12 h-12 rounded-md flex flex-col items-center justify-center shrink-0"
                   style={{ background: heatColor(s.avg_growth_pct) }}>
                <span className="mono text-sm font-medium text-black">{fmt.signed(s.avg_growth_pct)}</span>
                <span className="mono text-[9px] uppercase tracking-widest text-black/70">avg %</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-1.5">
                <TrophyIcon size={12} className="text-[#D6FF38]" />
                <span className="text-[#94A3B8]">{s.champions} champions</span>
              </div>
              <div className="flex items-center gap-1.5">
                <SkullIcon size={12} className="text-[#FF4D4D]" />
                <span className="text-[#94A3B8]">{s.graveyard} killed</span>
              </div>
            </div>

            {/* v6.5.0 family-progress sparkline strip */}
            <div className="mt-3 pt-3 border-t border-[#2A2D3A]">
              <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-2">
                Champion Goal (10 per family)
              </div>
              <FamilySparkline progress={s.family_progress} target={10} />
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2 text-xs pt-3 border-t border-[#2A2D3A]">
              <div>
                <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Best</div>
                <div className="mono text-[#2ECA7F] mt-0.5">{fmt.signed(s.best_growth_pct)}%</div>
              </div>
              <div>
                <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">Worst</div>
                <div className="mono text-[#FF4D4D] mt-0.5">{fmt.signed(s.worst_growth_pct)}%</div>
              </div>
              <div>
                <div className="text-[10px] mono uppercase tracking-widest text-[#64748B]">WR</div>
                <div className="mono text-[#94A3B8] mt-0.5">{s.avg_win_rate}%</div>
              </div>
            </div>

            <div className="mt-3 text-[11px] mono text-[#64748B]">
              best bot: <span className="text-[#F1F5F9] group-hover:text-[#D6FF38]">{s.best_bot}</span>
            </div>

            {s.variants && s.variants.length > 1 && (
              <div className="mt-2 text-[10px] mono text-[#64748B]">
                variants:{" "}
                {s.variants.slice(0, 4).map((v, i) => (
                  <span key={v.symbol} className="mr-2">
                    <span className="text-[#94A3B8]">{v.symbol}</span>
                    <span className="text-[#475569]">({v.total})</span>
                  </span>
                ))}
              </div>
            )}
          </Link>
        ))}
      </div>
    </div>
  );
}
